import React, { useMemo, useCallback, useState } from 'react';
import { useAppContext } from '../contexts/AppContext';
import * as d3 from 'd3';

// --- SHARED TYPE DEFINITIONS ---
export type GraphNodeKind = 'p' | 'f' | 'c-prev' | 'c-det' | 'd';

export const NODE_TYPE_MAP: Record<GraphNodeKind, string> = {
    p: 'Functions',
    f: 'FMEA Risks',
    'c-prev': 'Prevention Controls',
    'c-det': 'Detection Controls',
    d: 'DVP&R Tests',
};

export const NODE_STYLE_MAP: Record<GraphNodeKind, { fill: string, stroke: string, text: string }> = {
    'p': { fill: '#e8eaf6', stroke: '#3949ab', text: '#1a237e' },
    'f': { fill: '#ffebee', stroke: '#d32f2f', text: '#b71c1c' },
    'd': { fill: '#e8f5e9', stroke: '#388e3c', text: '#1b5e20' },
    'c-prev': { fill: 'var(--color-chip-prev-bg)', stroke: 'var(--color-chip-prev)', text: 'var(--color-text-primary)' },
    'c-det': { fill: 'var(--color-chip-det-bg)', stroke: 'var(--color-chip-det)', text: 'var(--color-text-primary)' },
};

export const LINK_COLORS = ['#90a4ae', 'var(--color-chip-prev)', 'var(--color-chip-det)', '#7cb342'];

export interface D3Node extends d3.SimulationNodeDatum {
    id: string;
    kind: GraphNodeKind;
    text: string;
    w: number;
    h: number;
    x?: number;
    y?: number;
    fx?: number | null;
    fy?: number | null;
}

export interface D3Link extends d3.SimulationLinkDatum<D3Node> {
    source: string | D3Node;
    target: string | D3Node;
    color: string;
    dash?: string;
}

/**
 * Shared hook for graph visualization data preparation.
 * Used by ExportPage and LinkExplorer.
 */
export function useGraphData() {
    const { allData, activeProject } = useAppContext();

    // Project filtering helper
    const isItemInProject = useCallback((projName?: string) => {
        if (!activeProject) return true;
        if (projName && projName === activeProject) return true;
        if (projName && (activeProject.includes(projName) || projName.includes(activeProject))) return true;
        const projectName = activeProject.split(' - ')[0];
        if (projName === projectName) return true;
        return false;
    }, [activeProject]);

    // Build nodes and links from data
    const { allNodes, allLinks } = useMemo(() => {
        const tempNodes: D3Node[] = [];
        const validNodeIds = new Set<string>();

        const measure = (text: string) => Math.min(300, Math.max(180, text.length * 7 + 40));

        const filteredPDiagrams = (allData?.pDiagramList || []).filter(p => {
            if (!isItemInProject(p.projectName)) return false;
            const status = p.status || 'active';
            return status === 'active';
        });
        const filteredFmeaItems = (allData?.fmeaItems || []).filter(f => isItemInProject(f.projectName));
        const filteredControls = (allData?.controls || []).filter(c => isItemInProject(c.projectName));
        const filteredDvprTests = (allData?.dvprTests || []).filter(d => isItemInProject(d.projectName));

        filteredPDiagrams.forEach(p => {
            tempNodes.push({ id: p.consolidatedFunctionId, kind: 'p', text: p.systemName, w: measure(p.systemName), h: 40 });
            validNodeIds.add(p.consolidatedFunctionId);
        });
        filteredFmeaItems.forEach(f => {
            tempNodes.push({ id: f.id, kind: 'f', text: f.failureMode, w: measure(f.failureMode), h: 40 });
            validNodeIds.add(f.id);
        });
        filteredControls.forEach(c => {
            tempNodes.push({ id: c.id, kind: c.type === 'prevention' ? 'c-prev' : 'c-det', text: c.name, w: measure(c.name), h: 40 });
            validNodeIds.add(c.id);
        });
        filteredDvprTests.forEach(d => {
            tempNodes.push({ id: d.testId, kind: 'd', text: d.testName, w: measure(d.testName), h: 40 });
            validNodeIds.add(d.testId);
        });

        const tempLinks: D3Link[] = [];
        const addLink = (source: string, target: string, color: string, dash?: string) => {
            if (validNodeIds.has(source) && validNodeIds.has(target)) {
                tempLinks.push({ source, target, color, dash });
            }
        };

        filteredFmeaItems.forEach(f => {
            addLink(f.function, f.id, '#90a4ae');
            (f.preventionControls || []).forEach(c => addLink(f.id, c, 'var(--color-chip-prev)'));
            (f.detectionControls || []).forEach(c => addLink(f.id, c, 'var(--color-chip-det)', '4,4'));
        });
        filteredDvprTests.forEach(d => {
            (d.traceableRiskId || []).forEach(fId => addLink(fId, d.testId, '#7cb342'));
        });

        return { allNodes: tempNodes, allLinks: tempLinks };
    }, [allData, isItemInProject]);

    return { allNodes, allLinks, isItemInProject };
}

/**
 * Shared hook for node selection logic with branch traversal.
 */
export function useNodeSelection(allNodes: D3Node[], allLinks: D3Link[]) {
    const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());

    // Get all nodes in the same branch (upstream + downstream)
    const getBranchIds = useCallback((startId: string): Set<string> => {
        const branch = new Set<string>([startId]);

        // Downstream (Source -> Target)
        const queueDown = [startId];
        const visitedDown = new Set<string>([startId]);
        while (queueDown.length > 0) {
            const curr = queueDown.shift()!;
            allLinks.forEach(l => {
                const s = (l.source as any).id || l.source;
                const t = (l.target as any).id || l.target;
                if (s === curr && !visitedDown.has(t)) {
                    visitedDown.add(t);
                    queueDown.push(t);
                    branch.add(t);
                }
            });
        }

        // Upstream (Target -> Source)
        const queueUp = [startId];
        const visitedUp = new Set<string>([startId]);
        while (queueUp.length > 0) {
            const curr = queueUp.shift()!;
            allLinks.forEach(l => {
                const s = (l.source as any).id || l.source;
                const t = (l.target as any).id || l.target;
                if (t === curr && !visitedUp.has(s)) {
                    visitedUp.add(s);
                    queueUp.push(s);
                    branch.add(s);
                }
            });
        }
        return branch;
    }, [allLinks]);

    const toggleNodeSelection = useCallback((nodeId: string, propagate = false) => {
        setSelectedNodes(prevSelected => {
            const newSet = new Set(prevSelected);
            const isSelected = newSet.has(nodeId);

            if (isSelected) {
                newSet.delete(nodeId);
                if (propagate) {
                    const branch = getBranchIds(nodeId);
                    branch.forEach(id => newSet.delete(id));
                }
            } else {
                newSet.add(nodeId);
                if (propagate) {
                    const branch = getBranchIds(nodeId);
                    branch.forEach(id => newSet.add(id));
                }
            }
            return newSet;
        });
    }, [getBranchIds]);

    const toggleColumnSelection = useCallback((kind: GraphNodeKind) => {
        const nodesInCol = allNodes.filter(n => n.kind === kind);
        setSelectedNodes(prevSelected => {
            const newSet = new Set(prevSelected);
            const allSelected = nodesInCol.every(n => prevSelected.has(n.id));

            nodesInCol.forEach(n => {
                if (allSelected) newSet.delete(n.id);
                else newSet.add(n.id);
            });
            return newSet;
        });
    }, [allNodes]);

    const selectAll = useCallback(() => {
        const allIds = allNodes.map(n => n.id);
        setSelectedNodes(prevSelected => {
            if (prevSelected.size === allIds.length) return new Set();
            return new Set(allIds);
        });
    }, [allNodes]);

    return { selectedNodes, setSelectedNodes, toggleNodeSelection, toggleColumnSelection, selectAll };
}
